import { Injectable } from '@angular/core';

import { STORAGE_KEYS } from '../../constants/storage-keys.const';
import { StorageUtil } from '../../utils/storage.utils';
import {
  AccountType,
  parseAccountType,
} from '../models/account-type.enum';

interface JwtPayload {
  exp?: number;
  sub?: string;
  /** Present on EWS access tokens (camelCase). */
  accountType?: string;
  account_type?: string;
}

@Injectable({ providedIn: 'root' })
export class TokenService {
  getToken(): string | null {
    return StorageUtil.getLocal<string>(STORAGE_KEYS.ACCESS_TOKEN);
  }

  setToken(token: string): void {
    StorageUtil.setLocal(STORAGE_KEYS.ACCESS_TOKEN, token);
  }

  getRefreshToken(): string | null {
    return StorageUtil.getLocal<string>(STORAGE_KEYS.REFRESH_TOKEN);
  }

  setRefreshToken(token: string): void {
    StorageUtil.setLocal(STORAGE_KEYS.REFRESH_TOKEN, token);
  }

  removeTokens(): void {
    StorageUtil.removeLocal(STORAGE_KEYS.ACCESS_TOKEN);
    StorageUtil.removeLocal(STORAGE_KEYS.REFRESH_TOKEN);
  }

  hasToken(): boolean {
    return !!this.getToken();
  }

  getAccountTypeFromToken(token?: string): AccountType | null {
    const tokenToCheck = token ?? this.getToken();
    if (!tokenToCheck) {
      return null;
    }
    const decoded = this.decodeToken(tokenToCheck);
    if (!decoded) {
      return null;
    }
    return parseAccountType(decoded.accountType ?? decoded.account_type);
  }

  decodeToken(token: string): JwtPayload | null {
    try {
      const payload = token.split('.')[1];
      if (!payload) {
        return null;
      }
      const base64 = payload.replaceAll('-', '+').replaceAll('_', '/');
      const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
      return JSON.parse(atob(padded)) as JwtPayload;
    } catch {
      return null;
    }
  }

  isTokenExpired(token?: string): boolean {
    const tokenToCheck = token ?? this.getToken();
    if (!tokenToCheck) {
      return true;
    }

    const decoded = this.decodeToken(tokenToCheck);
    if (!decoded?.exp) {
      return true;
    }

    return new Date(decoded.exp * 1000) <= new Date();
  }
}
